import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Clock, Truck, Percent, Star } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const defaultPromos = [
  {
    id: 'default_1',
    text: "Menu Midi à partir de 5,50€",
    subtext: "Du lundi au vendredi, 12h-13h",
    icon: 'Clock',
    isPopular: true
  },
  {
    id: 'default_2',
    text: "Livraison Gratuite",
    subtext: "Dès 15€ d'achat sur Uber Eats",
    icon: 'Truck',
    isPopular: false
  }
];

const iconMap = { Clock, Truck, Percent, Star };

const PromoBanner = () => {
  const [promos, setPromos] = useState(defaultPromos);
  const [currentPromo, setCurrentPromo] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    const fetchPromos = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/promos/active`);
        const backendPromos = (response.data.promos || []).map((p) => ({
          id: `promo_${p.code}`,
          text: `-${p.discount_percent}% avec le code ${p.code}`,
          subtext: p.description || "Offre exclusive",
          icon: 'Percent',
          isPopular: false,
          color: p.color_theme
        }));
        setPromos([...defaultPromos, ...backendPromos]);
      } catch {
        // Keep defaults on error
      }
    };
    fetchPromos();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      nextPromo();
    }, 5000);
    return () => clearInterval(interval);
  }, [currentPromo, promos.length]);

  const nextPromo = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentPromo((prev) => (prev + 1) % promos.length);
      setIsAnimating(false);
    }, 300);
  };

  const prevPromo = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentPromo((prev) => (prev - 1 + promos.length) % promos.length);
      setIsAnimating(false);
    }, 300);
  };

  const promo = promos[currentPromo];
  const IconComponent = iconMap[promo.icon] || Percent;

  return (
    <div className="fixed top-20 left-0 right-0 z-40 bg-gradient-to-r from-[#D4AF37] via-[#E5C158] to-[#D4AF37] shadow-md">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-center h-10 md:h-12 relative">
          <button
            onClick={prevPromo}
            className="absolute left-0 md:left-4 p-1 text-black/70 hover:text-black transition-colors"
            aria-label="Promotion précédente"
            data-testid="promo-prev-btn"
          >
            <ChevronLeft size={20} />
          </button>

          <div
            className={`flex items-center space-x-2 md:space-x-3 transition-all duration-300 ${
              isAnimating ? 'opacity-0 transform -translate-y-2' : 'opacity-100 transform translate-y-0'
            }`}
          >
            {promo.isPopular && (
              <span className="hidden md:inline-flex items-center space-x-1 bg-black text-[#D4AF37] px-2 py-0.5 rounded-full text-xs font-bold">
                <Star size={12} className="fill-[#D4AF37]" />
                <span>LE + POPULAIRE</span>
              </span>
            )}
            <IconComponent size={18} className="text-black hidden sm:block" />
            <div className="text-center">
              <span className="font-bold text-black text-sm md:text-base" data-testid="promo-text">
                {promo.text}
              </span>
              <span className="hidden sm:inline text-black/80 text-xs md:text-sm ml-2">
                — {promo.subtext}
              </span>
            </div>
          </div>

          <button
            onClick={nextPromo}
            className="absolute right-0 md:right-4 p-1 text-black/70 hover:text-black transition-colors"
            aria-label="Promotion suivante"
            data-testid="promo-next-btn"
          >
            <ChevronRight size={20} />
          </button>

          <div className="absolute -bottom-0 left-1/2 transform -translate-x-1/2 flex space-x-1">
            {promos.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setIsAnimating(true);
                  setTimeout(() => {
                    setCurrentPromo(index);
                    setIsAnimating(false);
                  }, 300);
                }}
                className={`w-1.5 h-1.5 rounded-full transition-all duration-200 ${
                  index === currentPromo ? 'bg-black w-3' : 'bg-black/40'
                }`}
                aria-label={`Promotion ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromoBanner;
